/**
 * Catálogo das telas rastreadas no e360 (JOR4-7826).
 *
 * No e360 o registro de uso é `tela` + `funcao` opcional: sem função é o
 * acesso à tela, com função é uma ação dentro dela. O código é a identidade
 * do item e o nome é só o rótulo exibido — o e360 guarda um nome por código e
 * o último que chega sobrescreve. Por isso os nomes moram aqui, fixos, e tudo
 * o que varia de um clique para outro vai no código, nunca no nome.
 *
 * Os códigos são os mesmos que o ePonto já enviava ao analytics do Release,
 * com os níveis unidos por ponto. Mudá-los partiria a série histórica, então
 * até os erros de digitação antigos (`relatorio_altoracoes_manuais`,
 * `historico_notificacoes_excessoes`) ficam como estão.
 */

export interface ItemRastreado {
    /** Identidade estável: snake_case, níveis separados por ponto. */
    codigo: string;
    nome: string;
}

const tela = (codigo: string, nome: string): ItemRastreado => ({
    codigo,
    nome,
});

const jornada = (caminho: string, nome: string) =>
    tela(`menu.jornada.${caminho}`, nome);
const embarcadores = (caminho: string, nome: string) =>
    tela(`menu.jornada_terceiros.${caminho}`, nome);

export const TELAS = {
    // Jornada
    inicio: jornada("inicio", "Início"),
    controleJornada: jornada(
        "controle_de_jornada_novo",
        "Controle da Jornada (Novo)",
    ),
    // O BETA herda o Novo inteiro e, até aqui, registrava com o código dele —
    // no e360 não dava para saber qual dos dois foi usado.
    controleJornadaGo: jornada(
        "controle_de_jornada_go",
        "Controle de Jornada (BETA)",
    ),
    manutencaoJornada: jornada(
        "manutencao_da_jornada",
        "Manutenção da Jornada",
    ),
    jornadaDetalhada: jornada("jornada_detalhada", "Jornada Detalhada"),
    jornadaAssistida: jornada("jornada_assistida", "Jornada Assistida"),
    controleJornadaAntigo: jornada(
        "controle_de_jornada",
        "Controle de Jornada (antigo)",
    ),
    assinaturaEletronica: jornada(
        "assinatura_eletronica",
        "Assinatura Eletrônica",
    ),
    dashboards: tela("menu.dashboards", "Dashboards"),
    paineisBi: jornada("paineis_bi", "BI - Painéis"),
    visaoBi: jornada("paineis_bi.visao", "BI - Visão"),

    // Cadastros
    empresas: jornada("cadastros.empresas", "Cadastro de Empresas"),
    motoristas: jornada("cadastros.motoristas", "Cadastro de Motoristas"),
    motoristasTecnologia: jornada(
        "cadastros.motoristas_tecnologia",
        "Motoristas (Tecnologia)",
    ),
    veiculos: jornada("cadastros.veiculos", "Cadastro de Veículos"),
    escalas: jornada("cadastros.escalas", "Cadastro de Escalas"),
    arvoreDecisao: jornada(
        "cadastros.arvore_de_decisao",
        "Árvore de Decisão",
    ),
    servicos: jornada("cadastros.servicos", "Cadastro de Serviços"),
    novidades: jornada("cadastros.novidades", "Novidades do Sistema"),
    embarcadores: jornada(
        "cadastros.embarcadores",
        "Cadastro de Embarcadores",
    ),
    cadastroBi: jornada("cadastros.paineis_bi", "BI - Cadastro"),
    indicadores: jornada(
        "cadastros.indicadores",
        "Gestão de Indicadores do Sistema",
    ),
    secoesIndicadores: jornada(
        "cadastros.secoes_de_indicadores",
        "Gestão de Seções de Indicadores",
    ),

    // Configurações
    chatAssistenteIa: jornada(
        "configuracoes.chat_assistente_ia",
        "Chat Assistente IA",
    ),
    telasSistema: jornada("configuracoes.telas", "Telas do Sistema"),
    menusSistema: jornada("configuracoes.menus", "Menus do Sistema"),
    usuariosSistema: jornada("configuracoes.usuarios", "Usuários do Sistema"),
    planosSistema: jornada("configuracoes.planos", "Planos do Sistema"),
    vinculosEmpresaPlano: jornada(
        "configuracoes.vinculos_empresa_plano",
        "Vínculos Empresa/Plano",
    ),
    permissoesSistema: jornada(
        "configuracoes.permissoes",
        "Permissões do Sistema",
    ),
    permissoesPorUsuario: jornada(
        "configuracoes.permissoes_por_usuario",
        "Permissões por Usuário",
    ),
    gruposEmpresas: jornada(
        "configuracoes.grupos_de_empresas",
        "Grupos de Empresas",
    ),
    vinculosEmbarcadoraPlano: jornada(
        "configuracoes.vinculos_embarcadora_plano",
        "Vínculos Embarcadora/Plano",
    ),
    mensagensAutomaticas: jornada(
        "configuracoes.mensagens_automaticas",
        "Mensagens Automáticas",
    ),
    historicoConfiguracoesJornada: jornada(
        "configuracoes.historico_configuracoes_jornada",
        "Histórico de Configurações da Jornada",
    ),

    // Relatórios
    listaRelatorios: jornada(
        "relatorios.lista_de_relatorios",
        "Lista de Relatórios",
    ),
    cartaoPontoSintetico: jornada(
        "relatorios.cartao_ponto_sintetico",
        "Cartão Ponto Sintético",
    ),
    cartaoPontoSinteticoGo: jornada(
        "relatorios.cartao_ponto_sintetico_go",
        "Cartão Ponto Sintético (GO)",
    ),
    cartaoPontoSinteticoAntigo: jornada(
        "relatorios.cartao_ponto_sintetico_antigo",
        "Cartão Ponto Sintético (antigo)",
    ),
    comprovantesRegistroPonto: jornada(
        "relatorios.comprovantes_registro_ponto",
        "Comprovantes de Registro de Ponto",
    ),
    relatorioAlteracoesManuais: jornada(
        "relatorios.relatorio_altoracoes_manuais",
        "Relatório de Alterações Manuais de Eventos",
    ),
    relatorioDsr: jornada("relatorios.relatorio_dsr", "Relatório de DSR"),
    relatorioProdutividade: jornada(
        "relatorios.relatorio_produtividade",
        "Relatório de Produtividade",
    ),
    relatorioKmDiariosMotoristas: jornada(
        "relatorios.relatorio_km_diarios_motoristas",
        "Relatório de KMs Diários dos Motoristas",
    ),
    relatorioFaltaDescansoRepouso: jornada(
        "relatorios.relatorio_falta_descanso_repouso",
        "Relatório de Horas Faltas - Repouso",
    ),
    relatorioHorasFalta: jornada(
        "relatorios.relatorio_horas_falta",
        "Relatório de Horas Falta - Refeição",
    ),
    relatorioEventos: jornada(
        "relatorios.relatorio_eventos",
        "Relatório de Eventos",
    ),
    relatorioOcorrencias: jornada(
        "relatorios.relatorio_ocorrencias",
        "Relatório de Ocorrências",
    ),
    relatorioAnomalias: jornada(
        "relatorios.relatorio_anomalias",
        "Relatório de Anomalias",
    ),
    adiamentoNotificacoesExcecoes: jornada(
        // Código herdado do analytics, fora do padrão das demais telas.
        "relatorios.controle_de_jornada_novo.adiamento_notificacoes_excecoes",
        "Adiamento de Notificações de Exceções",
    ),
    relatorioAfastamentos: jornada(
        "relatorios.relatorio_afastamentos",
        "Relatório de Afastamentos",
    ),
    relatorioAcessos: jornada(
        "relatorios.relatorio_acessos",
        "Relatório de Acessos",
    ),
    relatorioAtivacaoMotoristas: jornada(
        "relatorios.relatorio_ativacao_motoristas",
        "Relatório de Ativação de Motoristas",
    ),
    relatorioNaoConformidades: jornada(
        "relatorios.relatorio_nao_conformidades",
        "Relatório de Não Conformidades",
    ),
    relatorioAlteracoesViaMobile: jornada(
        "relatorios.relatorio_alteracoes_via_mobile",
        "Relatório de Alterações via Mobile",
    ),
    relatorioDiarias: jornada(
        "relatorios.relatorio_diarias",
        "Relatório de Diárias",
    ),
    relatorioMensagensJornada: jornada(
        "relatorios.relatorio_mensagens_jornada",
        "Relatório de Mensagens da Jornada",
    ),
    relatorioBancoDeHoras: jornada(
        "relatorios.relatorio_banco_de_horas",
        "Relatório de Banco de Horas",
    ),
    relatorioExcecoes: jornada(
        "relatorios.relatorio_excecoes",
        "Relatório de Exceções",
    ),
    relatorioTratativaExcecoes: jornada(
        "relatorios.relatorio_tratativa_excecoes",
        "Relatório de Tratativa de Exceções",
    ),
    relatorioAnotacoesJornada: jornada(
        "relatorios.relatorio_anotacoes_jornada",
        "Relatório de Anotações da Jornada",
    ),
    relatorioLogsAplicativo: jornada(
        "relatorios.relatorio_logs_aplicativo",
        "Relatório de Logs do Aplicativo",
    ),

    // Jornada de Embarcadores
    gestaoViagensTerceiros: embarcadores(
        "gestao_viagens",
        "Gestão de Viagens (Terceiros)",
    ),
    controleJornadaTerceiros: embarcadores(
        "controle_de_jornada",
        "Controle de Jornada (Terceiros)",
    ),
    pontuacaoPenalidadeTerceiros: embarcadores(
        "controle_pontuacao_penalidade",
        "Pontuação e Penalidade (Terceiros)",
    ),
    manutencaoJornadaTerceiros: embarcadores(
        "manutencao_da_jornada",
        "Manutenção da Jornada (Terceiros)",
    ),
    relatorioNaoConformidadesTerceiros: embarcadores(
        "relatorios.relatorio_nao_conformidades",
        "Não Conformidades (Terceiros)",
    ),
    relatorioJornadaTerceiros: embarcadores(
        "relatorios.relatorio_jornada",
        "Relatório de Jornada (Terceiros)",
    ),
} as const satisfies Record<string, ItemRastreado>;

/**
 * Transforma um identificador livre (classe de tela, título) num trecho de
 * código: snake_case, sem acento. Para o que precisa identificar o item no
 * código porque o nome não serve (ver o topo do arquivo).
 */
export const paraCodigo = (texto: string): string =>
    texto
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
        .replace(/[^A-Za-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase();
